import React, { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Linking } from 'react-native'
import { connect, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { NavLink } from 'react-router-dom'

import { isEmpty } from 'lodash-es'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ProviderExpeditedBookingConfig, ProviderUser } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { TypeAhead } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/typeAhead/TypeAhead'
import { TypeAheadFieldAdapter } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/typeAhead/TypeAheadFieldAdapter'
import { CircleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CircleIcon'
import { MessagingIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/MessagingIcon'
import { MoneyIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/MoneyIcon'
import { SideNavCalendarIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SideNavCalendarIcon'
import { SideNavCaseloadIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SideNavCaseloadIcon'
import { SideNavClientIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SideNavClientIcon'
import { SideNavDocumentIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SideNavDocumentIcon'
import { SideNavMessageIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SideNavMessageIcon'
import { SideNavToDoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SideNavToDoIcon'
import { ApplicationHeader } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/applicationHeader/ApplicationHeader'
import { Menu } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/menu/Menu'
import { SideNavigationBar } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/sideNavigationBar/SideNavigationBar'
import { TabDropdownProps } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/tabDropdown/TabDropdown'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './header.module.scss'
import PracticeAdminImpersonationBar from './PracticeAdminImpersonationBar'
import { actions, mixpanelEvents } from '../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../mixpanel/mixpanelTracking'
import { useGetCalendarProviderQuery, useLazyGetCalendarProviderQuery } from '../../../../calendar/data/calendarApi'
import { getShowCalendarTransitionModal } from '../../../../calendar/data/calendarSelectors'
import { useProviderCalendarLive } from '../../../../calendar/hooks/useProviderCalendarLive'
import { useNewDashboard } from '../../../../dashboard/hooks/useNewDashboard'
import { addAlert } from '../../../../data/alertActions'
import {
  getAuthConfig,
  getAuthEmploymentStatus,
  getAuthImpersonatedProviderId,
  getAuthIsLoggedIn,
  getAuthIsPracticeAdmin,
  getAuthLTProviders,
  getAuthPracticeAdmin,
  getAuthPracticeId,
  getAuthPracticeProviders,
  getAuthSupervisor,
  getAuthSupervisorSelectedProviderId,
  getAuthUser,
  getAuthUserId,
  getAuthVideoPreference,
} from '../../../../data/auth/authSelectors'
import { fetchPracticeProviders, setSelectedTimeZone, supervisorSelectProvider } from '../../../../data/authActions'
import { useLogoutMutation } from '../../../../data/authApi'
import { RootState } from '../../../../data/store'
import { useIsEngageDAProvider } from '../../../../hooks/useIsEngageDAProvider'
import { useShowClientsNav } from '../../../../hooks/useShowClientsNav'
import { useVideoSessionBannerHeight } from '../../../../hooks/useVideoSessionBannerHeight'
import { clearSelectedClientStore } from '../../../../lyraTherapy/clients/clientDetails/data/ltClientDetailsAutoActions'
import { getIsPrivacyGuardActive, getLTVideoSessionOpen } from '../../../../lyraTherapy/data/ltVideoSelectors'
import { useConversationsData } from '../../../../lyraTherapy/messages/data/hooks/useConversationsData'
import { getConversationsUnreadCount } from '../../../../lyraTherapy/messages/data/utils'
import { getProviderExpeditedBookingConfig } from '../../../../providers/data/providersDataActions'
import { getProviderDetailsCalendarState } from '../../../../providers/data/providerSelectors'
import { getProvider } from '../../../../providers/individualProvider/data/providerDetailsActions'
import { useGetVideoSessionPreferenceQuery } from '../../../../settings/settingsApi'
import {
  BCC_COACH_SUPERVISOR_ROLES,
  BCT_ROLES,
  BEND_ROLES,
  employmentTypes,
  LT_COACH_ROLES,
  ROLES,
} from '../../../constants/appConstants'
import {
  BC_CASELOAD,
  BC_MEASUREMENT_BASED_CARE,
  BC_PERFORMANCE,
  BC_PLATFORM_ENGAGEMENT,
  CARE_INSIGHTS,
  CLIENT_DOCUMENTS,
  CLIENTS,
  DASHBOARD,
  DOCUMENTS,
  DOWNLOADS,
  ELIGIBILITY_CHECKER,
  LC_CALENDAR_CURRENT,
  LC_CALENDAR_SELECT_CALENDARS,
  LT_CLIENTS,
  MESSENGER,
  MY_INFO,
  PAYMENT_HISTORY,
  PAYMENTS,
  PRACTICES,
  PROVIDERS,
  RECONCILER,
  REQUEST_PAYMENT,
  SCHEDULING,
  SETTINGS,
} from '../../../constants/routingConstants'
import { hydratePendoProviderMetadata } from '../../../utils/pendoProviderMetadata'
import { hasRole } from '../../../utils/utils'
import { DevMocksModal } from '../../devTools/DevMocksModal'
import { areDevToolsAvailable } from '../../devTools/devToolsAvailability'

const ApplicationHeaderContainer = styled.View(
  ({
    videoSessionOpen,
    videoSessionBannerHeight,
    isProviderSelectorOpen,
  }: {
    videoSessionOpen?: boolean
    videoSessionBannerHeight: number
    isProviderSelectorOpen: boolean
  }) => ({
    ...(videoSessionOpen && { marginTop: `${videoSessionBannerHeight}px` }),
    zIndex: isProviderSelectorOpen ? 11 : 6,
  }),
)

const ProviderDropdownContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginLeft: spacing['32px'],
  alignItems: 'center',
}))

const PracticeAdminDropdownContainer = styled.View<{ theme: ThemeType }>(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}))

const DropdownInputContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginLeft: spacing['16px'],
  position: 'relative',
}))

export const HeaderContainer = ({
  videoSessionOpen,
  appLocked,
  supervisorSelectProvider,
  getProvider,
  getProviderExpeditedBookingConfig,
  clearSelectedClientStore,
  fetchPracticeProviders,
  setSelectedTimeZone,
}: any) => {
  const { formatMessage } = useIntl()
  const userId = useSelector(getAuthUserId)
  const user = useSelector(getAuthUser)
  const config = useSelector(getAuthConfig)
  const supervisor = useSelector(getAuthSupervisor)
  const employmentStatus = useSelector(getAuthEmploymentStatus)
  const BCProviders = useSelector(getAuthLTProviders)
  const isPracticeAdmin = useSelector(getAuthIsPracticeAdmin)
  const practiceAdmin = useSelector(getAuthPracticeAdmin)
  const practiceProviders = useSelector(getAuthPracticeProviders)
  const practiceId = useSelector(getAuthPracticeId)
  const isLoggedIn = useSelector(getAuthIsLoggedIn)
  const impersonatedProviderId = useSelector(getAuthImpersonatedProviderId)
  const authSupervisorSelectedProviderId = useSelector(getAuthSupervisorSelectedProviderId)
  const [supervisorSelectedProviderId_, setSupervisorSelectedProviderId] = React.useState(
    authSupervisorSelectedProviderId,
  )
  const supervisorSelectedProviderId = supervisorSelectedProviderId_ ?? authSupervisorSelectedProviderId
  const [selectedProvider, setSelectedProvider] = React.useState<string>('')
  const [isProviderSelectorFocused, setIsProviderSelectorFocused] = React.useState(false)
  const [isSupervisorProviderSelectorFocused, setIsSupervisorProviderSelectorFocused] = React.useState(false)
  const [isDevMocksModalOpen, setIsDevMocksModalOpen] = React.useState(false)
  const { data: calendarProvider } = useGetCalendarProviderQuery({ providerId: userId }, { skip: !userId })
  const [getCalendarProviderForSelectedProvider] = useLazyGetCalendarProviderQuery()
  const isProviderCalendarLive = useProviderCalendarLive(config, calendarProvider)
  const providerCalendarState = useSelector(getProviderDetailsCalendarState)
  const showCalendarTransitionModal = useSelector(getShowCalendarTransitionModal)

  const videoPreference = useSelector(getAuthVideoPreference) ?? true
  const roles = user?.roles
  const id = user?.id
  const firstName = user?.first_name
  const lastName = user?.last_name
  const practiceName = user?.practice_name
  const isRegistered = user?.is_registered
  const isRegisteredForNav = isRegistered || !!impersonatedProviderId

  const outcomesEnabled = user?.outcomes_enabled
  const outcomesAgreed = user?.outcomes_agreed
  const schedulingEnabled = user?.scheduling_enabled
  const navigate = useNavigate()
  const location = useLocation()
  const [logout] = useLogoutMutation()
  const { colors } = useTheme()
  const isEngageDAProvider = useIsEngageDAProvider()
  const videoSessionBannerHeight = useVideoSessionBannerHeight()

  useEffect(() => {
    if (isPracticeAdmin && practiceId && isEmpty(practiceProviders)) {
      fetchPracticeProviders(practiceId)
    }
  }, [isPracticeAdmin, practiceId, practiceProviders, fetchPracticeProviders])

  const logoClickHandler = () => {
    return appLocked ? null : navigate('/')
  }

  const shouldSeeSupervisorSearch = !_.isEmpty(supervisor) && !_.isEmpty(BCProviders)

  const selectProvider = async (selections: { id: string }[] | undefined): Promise<void> => {
    if (!selections || selections.length === 0 || selections[0].id === supervisorSelectedProviderId) return
    const providerSummary = BCProviders.find((p) => p.id === selections[0].id)
    if (!providerSummary) return

    setSupervisorSelectedProviderId(selections[0].id)
    const providerData = await getProvider(providerSummary.id)
    const providerDetails = providerData?.data?.data as Partial<ProviderUser> | undefined
    const expeditedBookingConfig = await getProviderExpeditedBookingConfig({ id: providerSummary.id })
      .then((config: ProviderExpeditedBookingConfig) => {
        return config
      })
      .catch(() => {
        return null
      })
    const provider: ProviderUser = {
      ...hydratePendoProviderMetadata(providerSummary, providerDetails),
      employment_status: providerDetails?.employment_status ?? providerSummary.employment_status,
      expedited_booking_config: expeditedBookingConfig ?? providerSummary.expedited_booking_config,
      outcomes_agreed: providerDetails?.outcomes_agreed ?? providerSummary.outcomes_agreed,
      country: providerDetails?.country ?? providerSummary.country,
    }

    const shouldSetProviderTimeZone = isTimezoneSelectorEnabled && config?.inProductCalendar?.showTimezoneSelector
    const providerTimeZone = shouldSetProviderTimeZone
      ? (await getCalendarProviderForSelectedProvider({ providerId: provider.id })).data?.timezone
      : undefined

    supervisorSelectProvider(provider)
    if (providerTimeZone) {
      setSelectedTimeZone(providerTimeZone)
    }
    clearSelectedClientStore()
    const isBendProvider = hasRole(provider.roles, BEND_ROLES)
    navigate(isBendProvider ? LC_CALENDAR_CURRENT.route : LT_CLIENTS.route)
  }

  const selectPracticeProvider = async (selections: { id: string; label: string }[] | undefined): Promise<void> => {
    if (!selections || selections?.length !== 1 || selections?.[0]?.id === id) return
    const providerSummary = practiceProviders.find(({ id }: any) => id === selections[0].id)
    if (!providerSummary) return

    try {
      const providerData = await getProvider(providerSummary.id)
      const providerDetails = providerData?.data?.data as Partial<ProviderUser> | undefined

      const provider: ProviderUser = {
        ...hydratePendoProviderMetadata(providerSummary, providerDetails),
        employment_status: providerDetails?.employment_status ?? providerSummary.employment_status,
        in_person_preference: providerDetails?.in_person_preference ?? providerSummary.in_person_preference,
        video_preference: providerDetails?.video_preference ?? providerSummary.video_preference,
      }

      supervisorSelectProvider(provider)
      clearSelectedClientStore()
      navigate(MY_INFO.route)
    } catch (error) {
      console.error('Failed to fetch provider data:', error)
      addAlert({
        show: true,
        contents: 'Failed to fetch provider data: ' + error,
        style: 'danger',
        expires: true,
      })
    }
  }

  const returnToPracticeAdminView = useCallback(() => {
    if (practiceAdmin) {
      supervisorSelectProvider(practiceAdmin)
      clearSelectedClientStore()
      setSelectedProvider('')
      navigate(REQUEST_PAYMENT.route)
    }
  }, [practiceAdmin, supervisorSelectProvider, clearSelectedClientStore, navigate])

  const openGoSearch = () => {
    document.body.dispatchEvent(new CustomEvent('openGoSearch'))
  }

  const isBCCSupervisor =
    supervisor && !isEmpty(supervisor.roles) && hasRole(supervisor.roles, BCC_COACH_SUPERVISOR_ROLES)

  const newCaseloadDashboardVersion =
    hasRole(roles, LT_COACH_ROLES) &&
    employmentStatus &&
    [employmentTypes.FULL_TIME, employmentTypes.PART_TIME].includes(employmentStatus)

  let loggedInButtons: any = []
  let tabs: TabDropdownProps[] = []
  const dropDownItems = []
  const menuItems = []
  const logoItems = []
  const practiceItems = []
  const hideLoggedInButtons = !!location.state && location.state.withoutNav
  const {
    showPlatformEngagementDashboard,
    isInProductCalendarEnabled,
    isDACalendarTransitionEnabled,
    isSideNavigationBarEnabled,
    isPracticeAdminImpersonationEnabled,
    isCareInsightsVisible,
    isDAEngageOnOffPlatformVideoEnabled,
    isTimezoneSelectorEnabled,
  } = useFlags()

  const showClientsNav = useShowClientsNav()

  const { isError, isLoading } = useGetVideoSessionPreferenceQuery(
    { id: userId },
    {
      skip: !isDAEngageOnOffPlatformVideoEnabled || !userId || !config?.supportsOnOffPlatformVideo || !videoPreference,
    },
  )
  const hasSetVideoSessionPreference = !isError && !isLoading

  const showNewDashboard = useNewDashboard()
  // Here, to choose routing for the header calendar tab, we use the following:
  // 1. If the IPC flag is true and they were not live on smart scheduling they get the IPC current calendar page
  // 2. If both IPC and transition flag are true, they are not live, and they have seen the modal on RP form they go
  //    directly to the select calendars page
  // 3. If the IPC flag and transition flag are true, and they are live or they have not seen the modal on RP form they
  //    get the IPC current calendar page
  // 4. All else, go to smart scheduling
  const calendarRouteForDA =
    isInProductCalendarEnabled && providerCalendarState != 'ProviderCalendarStatus.ONLINE_BOOKING_LIVE'
      ? LC_CALENDAR_CURRENT.route
      : isDACalendarTransitionEnabled &&
          providerCalendarState === 'ProviderCalendarStatus.ONLINE_BOOKING_LIVE' &&
          isInProductCalendarEnabled &&
          !isProviderCalendarLive &&
          !showCalendarTransitionModal
        ? LC_CALENDAR_SELECT_CALENDARS.route
        : isDACalendarTransitionEnabled && isInProductCalendarEnabled
          ? LC_CALENDAR_CURRENT.route
          : SCHEDULING.route

  // Only get conversation data if the user is logged in and has access to the messaging center through the side bar navigation
  const { data: conversations } = useConversationsData(undefined, {
    skip: !isLoggedIn || !config?.bcPlatform || !isSideNavigationBarEnabled || config?.hideMessagesNav,
  })
  const unreadMessageCount = getConversationsUnreadCount(conversations)

  if (isRegisteredForNav && !hideLoggedInButtons) {
    if (hasRole(roles, [ROLES.INDIVIDUAL_PROVIDER, ROLES.PRACTICE_PROVIDER, ROLES.ICAS_PROVIDER])) {
      if ((outcomesEnabled || hasRole(roles, ROLES.ICAS_PROVIDER)) && config?.directAccessClientList !== false) {
        loggedInButtons.push(
          <li key={_.uniqueId('headerNavLink_')}>
            <NavLink className='updated-look-feel' to={CLIENTS.route} data-test-id='Header-DAClientslink'>
              Clients
              {!outcomesAgreed && (
                <svg className='nav-flag' height='6' width='6'>
                  <circle cx='3' cy='3' r='3' fill={styles.x_alert} />
                </svg>
              )}
            </NavLink>
          </li>,
        )
        tabs.push({
          activePathname: CLIENTS.route,
          currentPathname: location.pathname,
          testID: 'Header-DAClientslink',
          text: 'Clients',
          onPress: () => {
            track({ event: mixpanelEvents.OPEN_CLIENT_LIST })
            navigate(CLIENTS.route)
          },
          _icon: <SideNavClientIcon size={32} />,
        })
      }
    }

    // Reconciler
    if (hasRole(roles, ROLES.RECONCILER)) {
      loggedInButtons.push(
        <li>
          <NavLink className='updated-look-feel' key={_.uniqueId('headerNavLink_')} to={RECONCILER.route}>
            ID Reconciler
          </NavLink>
        </li>,
      )
      tabs.push({
        activePathname: RECONCILER.route,
        currentPathname: location.pathname,
        testID: 'Header-idReconcilerLink',
        text: 'ID Reconciler',
        onPress: () => navigate(RECONCILER.route),
        _icon: <CircleIcon size={32} />,
      })
    }

    if (config?.requestPayment) {
      loggedInButtons.push(
        <li key={_.uniqueId('headerNavLink_')}>
          <NavLink className='updated-look-feel' to={REQUEST_PAYMENT.route}>
            Request Payment
          </NavLink>
        </li>,
      )
      loggedInButtons.push(
        <li key={_.uniqueId('headerNavLink_')}>
          <NavLink className='updated-look-feel' to={PAYMENT_HISTORY.route}>
            Payment History
          </NavLink>
        </li>,
      )
      tabs.push({
        activePathname: REQUEST_PAYMENT.route,
        currentPathname: location.pathname,
        testID: 'Header-requestPaymentLink',
        text: 'Request Payment',
        onPress: () => navigate(REQUEST_PAYMENT.route),
        _icon: <CircleIcon size={32} />,
      })
      tabs.push({
        activePathname: PAYMENT_HISTORY.route,
        currentPathname: location.pathname,
        testID: 'Header-paymentHistoryLink',
        text: 'Payment History',
        onPress: () => navigate(PAYMENT_HISTORY.route),
        _icon: <CircleIcon size={32} />,
      })
    }

    // All DA-related roles
    if (config?.eligibilityChecker) {
      loggedInButtons.push(
        <li key={_.uniqueId('headerNavLink_')}>
          <NavLink className='updated-look-feel' to={ELIGIBILITY_CHECKER.route} data-test-id='Header-eligibilityLink'>
            Eligibility
          </NavLink>
        </li>,
      )
      tabs.push({
        activePathname: ELIGIBILITY_CHECKER.route,
        currentPathname: location.pathname,
        testID: 'Header-eligibilityLink',
        text: 'Eligibility',
        onPress: () => navigate(ELIGIBILITY_CHECKER.route),
        _icon: <CircleIcon size={32} />,
      })
    }

    // Individual and Practice Providers; Excluding BC Providers but including ICAS Providers with their own config check
    if (config?.smartSchedulingEnabled || config?.internationalSmartSchedulingEnabled) {
      tabs.push({
        activePathname: calendarRouteForDA,
        currentPathname: location.pathname,
        testID: calendarRouteForDA === SCHEDULING.route ? 'Header-calendarLink' : 'Header-lcCalendarLink',
        text: 'Calendar',
        onPress: () => {
          track({ event: mixpanelEvents.OPEN_CALENDAR_TAB })
          navigate(calendarRouteForDA)
        },
        _icon: <SideNavCalendarIcon size={32} />,
      })
      // Add settings for not previously connected providers + those with transition flag
      if (
        isInProductCalendarEnabled &&
        (providerCalendarState != 'ProviderCalendarStatus.ONLINE_BOOKING_LIVE' || isDACalendarTransitionEnabled)
      ) {
        menuItems.push({
          text: 'Settings',
          id: 'menuSettings',
          testId: 'Header-menuSettings',
          onPressItem: () => {
            track({ event: mixpanelEvents.OPEN_SETTINGS })
            navigate(SETTINGS.route)
          },
        })
      }
    }

    // Both Individual Provider and Practice Provider
    if (hasRole(roles, [ROLES.INDIVIDUAL_PROVIDER, ROLES.PRACTICE_PROVIDER])) {
      // We only push this to dropdown for DA providers if the phase 1 flag is false, additionally only push for ICAS
      // providers if their flag is also false.
      if (!config?.smartSchedulingEnabled && !config?.internationalSmartSchedulingEnabled) {
        if (schedulingEnabled) {
          if (!isInProductCalendarEnabled) {
            menuItems.push({
              text: 'Calendar',
              id: 'menuCalendar',
              testId: 'Header-menuCalendar',
              onPressItem: () => {
                track({ event: mixpanelEvents.BUTTON_PRESS, action: actions.CALENDAR_DROPDOWN })
                navigate(SCHEDULING.route)
              },
            })
          }
        }
      }
    }

    // Practice Admin and Individual Provider (but not BEND providers)
    if (hasRole(roles, [ROLES.PRACTICES_ADMIN, ROLES.INDIVIDUAL_PROVIDER]) && !hasRole(roles, BEND_ROLES)) {
      dropDownItems.push(
        <li key={_.uniqueId('headerNavLink_')}>
          <NavLink className='updated-look-feel' to={DOWNLOADS.route}>
            Downloads
          </NavLink>
        </li>,
      )
      menuItems.push({
        text: 'Downloads',
        id: 'menuDownloads',
        testId: 'Header-menuDownloads',
        onPressItem: () => {
          track({ event: mixpanelEvents.OPEN_DOWNLOADS })
          navigate(DOWNLOADS.route)
        },
      })
    }

    // Individual Provider, Practice Provider and Practice Admin
    if (hasRole(roles, [ROLES.INDIVIDUAL_PROVIDER, ROLES.PRACTICE_PROVIDER, ROLES.PRACTICES_ADMIN])) {
      // Hide My Info for Bend providers (NP and Core providers)
      if (!hasRole(roles, BEND_ROLES)) {
        dropDownItems.push(
          <li key={_.uniqueId('headerNavLink_')}>
            <NavLink className='updated-look-feel' to={MY_INFO.route}>
              My Info
            </NavLink>
          </li>,
        )
        menuItems.push({
          text: 'My Info',
          id: 'menuMyInfo',
          testId: 'Header-menuMyInfo',
          onPressItem: () => {
            track({ event: mixpanelEvents.OPEN_MY_INFO })
            navigate(MY_INFO.route)
          },
        })
      }
      dropDownItems.push(
        <li key={_.uniqueId('headerNavLink_')}>
          <a
            className='updated-look-feel'
            target='_blank'
            href={config?.helpCenterUrl || 'https://provider-support.lyrahealth.com/'}
            rel='noreferrer'
          >
            Help Center
          </a>
        </li>,
      )
      if (!isSideNavigationBarEnabled || (isSideNavigationBarEnabled && !config?.bcPlatform)) {
        menuItems.push({
          text: 'Help Center',
          id: 'menuHelpCenter',
          testId: 'Header-menuHelpCenter',
          onPressItem: () => {
            track({
              event: mixpanelEvents.BUTTON_PRESS,
              action: actions.HELP_CENTER_DROPDOWN,
              details: { helpCenterURL: config?.helpCenterUrl || 'https://provider-support.lyrahealth.com/' },
            })
            Linking.openURL(config?.helpCenterUrl || 'https://provider-support.lyrahealth.com/')
          },
        })
      }
    }

    // Both Providers Admin and Payment Admin
    if (hasRole(roles, [ROLES.PROVIDERS_ADMIN, ROLES.PAYMENTS_ADMIN])) {
      loggedInButtons.push(
        <li>
          <NavLink className='updated-look-feel' key={_.uniqueId('headerNavLink_')} to={PROVIDERS.route}>
            Providers
          </NavLink>
        </li>,
      )
      loggedInButtons.push(
        <li>
          <NavLink className='updated-look-feel' key={_.uniqueId('headerNavLink_')} to={PRACTICES.route}>
            Practices
          </NavLink>
        </li>,
      )
      tabs.push({
        activePathname: PROVIDERS.route,
        currentPathname: location.pathname,
        testID: 'Header-providersLink',
        text: 'Providers',
        onPress: () => navigate(PROVIDERS.route),
        _icon: <CircleIcon size={32} />,
      })
      tabs.push({
        activePathname: PRACTICES.route,
        currentPathname: location.pathname,
        testID: 'Header-practicesLink',
        text: 'Practices',
        onPress: () => navigate(PRACTICES.route),
        _icon: <CircleIcon size={32} />,
      })
    }

    // Payments Admin only
    if (hasRole(roles, ROLES.PAYMENTS_ADMIN)) {
      loggedInButtons.push(
        <li>
          <NavLink className='updated-look-feel' key={_.uniqueId('headerNavLink_')} to={PAYMENTS.route}>
            Payments
          </NavLink>
        </li>,
      )
      tabs.push({
        activePathname: PAYMENTS.route,
        currentPathname: location.pathname,
        testID: 'Header-paymentsLink',
        text: 'Payments',
        onPress: () => navigate(PAYMENTS.route),
        _icon: <CircleIcon size={32} />,
      })
    }

    // Lyra Therapy
    if (config?.bcPlatform) {
      const inVideoSessionForbiddenLinkProps = appLocked
        ? { onClick: (e: any) => e.preventDefault(), style: { cursor: 'not-allowed' } }
        : {}

      loggedInButtons = [
        ...(!config?.hideTodoPage || showNewDashboard
          ? [
              <li key={_.uniqueId('headerNavLink_')}>
                <NavLink
                  className='updated-look-feel'
                  to={DASHBOARD.route}
                  {...inVideoSessionForbiddenLinkProps}
                  data-test-id='Header-todoLink'
                >
                  {showNewDashboard ? 'Home' : 'To-do'}
                </NavLink>
              </li>,
            ]
          : []),
        <li key={_.uniqueId('headerNavLink_')}>
          <NavLink
            className='updated-look-feel'
            to={LT_CLIENTS.route}
            {...inVideoSessionForbiddenLinkProps}
            data-test-id='Header-clientsLink'
          >
            Clients
          </NavLink>
        </li>,
      ]
      tabs = [
        ...(!config?.hideTodoPage || showNewDashboard
          ? [
              {
                activePathname: DASHBOARD.route,
                currentPathname: location.pathname,
                disabled: appLocked,
                text: showNewDashboard ? 'Home' : 'To-do',
                testID: 'Header-todoLink',
                onPress: () => navigate(DASHBOARD.route),
                _icon: <SideNavToDoIcon size={32} />,
              },
            ]
          : []),
      ]

      if (isInProductCalendarEnabled) {
        tabs.push({
          activePathname: LC_CALENDAR_CURRENT.route,
          currentPathname: location.pathname,
          testID: 'Header-lcCalendarLink',
          disabled: appLocked,
          text: 'Calendar',
          onPress: () => {
            track({ event: mixpanelEvents.OPEN_CALENDAR_TAB })
            navigate(LC_CALENDAR_CURRENT.route)
          },
          _icon: <SideNavCalendarIcon size={32} />,
        })
        menuItems.push({
          text: 'Settings',
          id: 'menuSettings',
          testId: 'Header-menuSettings',
          onPressItem: () => {
            track({ event: mixpanelEvents.OPEN_SETTINGS })
            navigate(SETTINGS.route)
          },
        })
      }
      if (showClientsNav) {
        tabs.push({
          activePathname: LT_CLIENTS.route,
          currentPathname: location.pathname,
          disabled: appLocked,
          text: 'Clients',
          testID: 'Header-clientsLink',
          onPress: () => {
            track({ event: mixpanelEvents.OPEN_CLIENT_LIST })
            navigate(LT_CLIENTS.route)
          },
          _icon: <SideNavClientIcon size={32} />,
        })
      }
      if (!config?.hideMessagesNav) {
        tabs.push({
          activePathname: MESSENGER.route,
          currentPathname: location.pathname,
          disabled: appLocked,
          testID: 'Header-messengerLink',
          text: 'Messages',
          onPress: () => {
            track({ event: mixpanelEvents.OPEN_MESSAGE_CENTER })
            navigate(MESSENGER.route)
          },
          _icon: appLocked ? (
            <MessagingIcon fillColor={colors.iconInactive} size={32} disableMessageCount={unreadMessageCount > 0} />
          ) : (
            <SideNavMessageIcon unreadMessageCount={unreadMessageCount} size={32} />
          ),
        })
      }
      if (!config?.hideDocumentsNav) {
        tabs.push({
          activePathname: DOCUMENTS.route,
          activePathnameExceptions: [CLIENT_DOCUMENTS.route],
          currentPathname: location.pathname,
          disabled: isEngageDAProvider && appLocked,
          testID: 'Header-documentsLink',
          text: 'Documents',
          onPress: () => {
            track({ event: mixpanelEvents.BUTTON_PRESS, action: actions.OPEN_DOCUMENTS })
            navigate(DOCUMENTS.route)
          },
          _icon: <SideNavDocumentIcon size={32} />,
        })
      }
      if (config.utilizationEnabled) {
        loggedInButtons.push(
          <li key={_.uniqueId('headerNavLink_')}>
            <NavLink
              className='updated-look-feel'
              to={BC_PERFORMANCE.route}
              {...inVideoSessionForbiddenLinkProps}
              data-test-id='Header-caseloadLink'
            >
              Caseload
            </NavLink>
          </li>,
        )
        if (isBCCSupervisor) {
          tabs.push({
            activePathname: newCaseloadDashboardVersion ? BC_CASELOAD.route : BC_PERFORMANCE.route,
            activePathnameExceptions: [BC_MEASUREMENT_BASED_CARE.route, BC_PLATFORM_ENGAGEMENT.route],
            currentPathname: location.pathname,
            disabled: appLocked,
            onPress: () => (newCaseloadDashboardVersion ? navigate(BC_CASELOAD.route) : navigate(BC_PERFORMANCE.route)),
            text: 'Caseload',
            testID: 'Header-caseloadLink',
            _icon: <SideNavCaseloadIcon size={32} />,
          })
        }
        if (!isBCCSupervisor) {
          tabs.push({
            activePathname: BC_PERFORMANCE.route,
            currentPathname: location.pathname,
            disabled: appLocked,
            onPress: () => navigate(BC_PERFORMANCE.route),
            text: 'Caseload',
            testID: 'Header-caseloadLink',
            dropdownWidth: 217,
            _icon: <SideNavCaseloadIcon size={32} />,
            subTabs: [
              ...(hasRole(roles, BCT_ROLES)
                ? [
                    {
                      activePathname: BC_PERFORMANCE.route,
                      activePathnameExceptions: [BC_MEASUREMENT_BASED_CARE.route, BC_PLATFORM_ENGAGEMENT.route],
                      currentPathname: location.pathname,
                      text: 'Caseload',
                      onPress: () => navigate(BC_PERFORMANCE.route),
                    },
                    {
                      activePathname: BC_MEASUREMENT_BASED_CARE.route,
                      currentPathname: location.pathname,
                      text: 'Measurement Based Care',
                      onPress: () => navigate(BC_MEASUREMENT_BASED_CARE.route),
                    },
                  ]
                : []),
              ...(showPlatformEngagementDashboard && hasRole(roles, BCT_ROLES)
                ? [
                    {
                      activePathname: BC_PLATFORM_ENGAGEMENT.route,
                      currentPathname: location.pathname,
                      text: 'Platform Engagement',
                      onPress: () => navigate(BC_PLATFORM_ENGAGEMENT.route),
                    },
                  ]
                : []),
              ...(isCareInsightsVisible && config?.showCareInsights
                ? [
                    {
                      activePathname: CARE_INSIGHTS.route,
                      currentPathname: location.pathname,
                      text: formatMessage({
                        defaultMessage: 'Care Insights',
                        description: 'Navigation menu item for Care Insights dashboard',
                      }),
                      onPress: () => navigate(CARE_INSIGHTS.route),
                    },
                  ]
                : []),
            ],
          })
        }
      }

      if (config.requestPayment) {
        loggedInButtons.push(
          <li>
            <NavLink
              className='updated-look-feel'
              key={_.uniqueId('headerNavLink_')}
              to={REQUEST_PAYMENT.route}
              {...inVideoSessionForbiddenLinkProps}
              data-test-id='Header-requestPaymentLink'
            >
              Request Payment
            </NavLink>
          </li>,
        )
        loggedInButtons.push(
          <li>
            <NavLink
              className='updated-look-feel'
              key={_.uniqueId('headerNavLink_')}
              to={PAYMENT_HISTORY.route}
              {...inVideoSessionForbiddenLinkProps}
              data-test-id='Header-paymentHistoryLink'
            >
              Payment History
            </NavLink>
          </li>,
        )
        tabs.push({
          activePathname: REQUEST_PAYMENT.route,
          currentPathname: location.pathname,
          disabled: appLocked,
          testID: 'Header-billing',
          text: 'Billing',
          onPress: () => navigate(REQUEST_PAYMENT.route),
          _icon: <MoneyIcon size={32} />,
          subTabs: [
            {
              activePathname: REQUEST_PAYMENT.route,
              currentPathname: location.pathname,
              disabled: appLocked,
              testID: 'Header-requestPaymentLink',
              text: 'Legacy Payment Form',
              onPress: () => {
                if (isEngageDAProvider) {
                  track({ event: mixpanelEvents.OPEN_LEGACY_REQUEST_PAYMENT_FORM })
                }
                navigate(REQUEST_PAYMENT.route)
              },
              _icon: <CircleIcon size={32} />,
            },
            {
              activePathname: PAYMENT_HISTORY.route,
              currentPathname: location.pathname,
              disabled: appLocked,
              testID: 'Header-paymentHistoryLink',
              text: 'Payment History',
              onPress: () => {
                if (isEngageDAProvider) {
                  track({ event: mixpanelEvents.OPEN_PAYMENT_HISTORY })
                }
                navigate(PAYMENT_HISTORY.route)
              },
              _icon: <CircleIcon size={32} />,
            },
            ...(config?.eligibilityChecker
              ? [
                  {
                    activePathname: ELIGIBILITY_CHECKER.route,
                    currentPathname: location.pathname,
                    testID: 'Header-eligibilityLink',
                    text: 'Eligibility',
                    onPress: () => {
                      if (isEngageDAProvider) {
                        track({ event: mixpanelEvents.OPEN_ELIGIBILITY_CHECKER })
                      }
                      navigate(ELIGIBILITY_CHECKER.route)
                    },
                    _icon: <CircleIcon size={32} />,
                  },
                ]
              : []),
          ],
        })
      }
    }

    // LT Supervisor
    if (shouldSeeSupervisorSearch) {
      const providerlistDropdown = BCProviders.map((provider: ProviderUser) => ({
        label: `${provider.first_name} ${provider.last_name} - ${provider.id}`,
        id: provider.id,
      }))

      logoItems.push(
        <ProviderDropdownContainer>
          <BodyText size={BodyTextSize.LARGE} text='Provider:' />
          <DropdownInputContainer>
            <TypeAheadFieldAdapter
              placeholder='Select a provider...'
              emptyLabel='No provider found'
              multiSelect={false}
              options={providerlistDropdown}
              input={{
                onChange: selectProvider,
                onFocus: () => setIsSupervisorProviderSelectorFocused(true),
                onBlur: () => setIsSupervisorProviderSelectorFocused(false),
                value: supervisorSelectedProviderId
                  ? providerlistDropdown.find((p) => p.id === supervisorSelectedProviderId)
                  : undefined,
                name: 'supervisor-selector-for-provider',
              }}
              baseInputStyle={{ marginBottom: 0 }}
              inputContainerStyle={{ minWidth: 300 }}
              dropdownContainerStyle={{ maxWidth: 400 }}
              fixedDropdownMenuPosition
            />
          </DropdownInputContainer>
        </ProviderDropdownContainer>,
      )
    }

    //DA Practice Admin
    if (isPracticeAdminImpersonationEnabled && isPracticeAdmin) {
      const practiceProviderStringOptions = practiceProviders.reduce((res: any, provider: any) => {
        const label = `${provider.first_name} ${provider.last_name}`
        return [...res, label]
      }, [])

      const handlePracticeAdminProviderChange = (value: string) => {
        setSelectedProvider(value)
        const provider = practiceProviders.find((p: any) => `${p.first_name} ${p.last_name}` === value)
        if (provider) {
          selectPracticeProvider([{ id: provider.id, label: value }])
        }
      }

      practiceItems.push(
        <PracticeAdminDropdownContainer
          key='practice-admin-provider-selector'
          testID={tID('PracticeAdminProviderSelector')}
        >
          <DropdownInputContainer>
            <TypeAhead
              name='practice-admin-provider-selector'
              placeholder='Select a provider...'
              options={practiceProviderStringOptions}
              value={selectedProvider}
              onChange={handlePracticeAdminProviderChange}
              onFocus={() => setIsProviderSelectorFocused(true)}
              onBlur={() => setIsProviderSelectorFocused(false)}
              isFocused={isProviderSelectorFocused}
              multiSelect={false}
              fixedDropdownMenuPosition={true}
              baseInputStyle={{ marginBottom: 0 }}
              inputContainerStyle={{
                minWidth: 343,
              }}
            />
          </DropdownInputContainer>
        </PracticeAdminDropdownContainer>,
      )
    }
  } // end is_registered

  if (isLoggedIn && areDevToolsAvailable()) {
    menuItems.push({
      text: 'Dev mocks',
      id: 'menuDevMocks',
      testId: 'Header-menuDevMocks',
      onPressItem: () => setIsDevMocksModalOpen(true),
    })
  }

  if (isLoggedIn) {
    menuItems.push({
      text: 'Log out',
      id: 'menuLogOut',
      testId: 'Header-menuLogOut',
      onPressItem: () => {
        logout({ clearLastPage: true })
      },
    })
  }

  const hasInAppCalendarSettings = menuItems.some((item) => item.id === 'menuSettings')

  // Remove the settings item when hasInAppCalendarSettings is true
  const filteredMenuItems = menuItems.filter((item) => {
    return !(hasInAppCalendarSettings && item.id === 'menuSettings')
  })

  return (
    <>
      <ApplicationHeaderContainer
        videoSessionOpen={videoSessionOpen}
        videoSessionBannerHeight={videoSessionBannerHeight}
        isProviderSelectorOpen={isProviderSelectorFocused || isSupervisorProviderSelectorFocused}
      >
        {isPracticeAdminImpersonationEnabled && isPracticeAdmin && (
          <PracticeAdminImpersonationBar
            defaultProviderView={!!impersonatedProviderId}
            logoItem={practiceItems}
            onReturnToAdminView={returnToPracticeAdminView}
          />
        )}
        {/* Show ApplicationHeader either when side nav is disabled, or when side nav is enabled and user meets role/supervisor conditions */}
        {!isSideNavigationBarEnabled || !config?.bcPlatform || shouldSeeSupervisorSearch ? (
          <ApplicationHeader
            currentPathname={location.pathname}
            handleLogoClick={logoClickHandler}
            tabs={isSideNavigationBarEnabled && shouldSeeSupervisorSearch ? [] : tabs}
            title={
              !isSideNavigationBarEnabled || (isSideNavigationBarEnabled && !config?.bcPlatform)
                ? practiceName
                  ? `${practiceName}`
                  : firstName || lastName
                    ? `${firstName} ${lastName}`
                    : undefined
                : undefined
            }
            logoItems={logoItems}
            menu={
              menuItems.length > 0 &&
              (!isSideNavigationBarEnabled || (isSideNavigationBarEnabled && !config?.bcPlatform)) ? (
                <Menu
                  menuItems={menuItems}
                  testID={'Header-menu'}
                  horizontalPosition='right'
                  disableBottomSheetForWeb
                />
              ) : undefined
            }
          />
        ) : null}
        {isSideNavigationBarEnabled && config?.bcPlatform && (
          <SideNavigationBar
            handleLogoClick={logoClickHandler}
            navigationItems={tabs}
            title={practiceName ? `${practiceName}` : firstName || lastName ? `${firstName} ${lastName}` : undefined}
            menuItemsList={filteredMenuItems}
            onSupportPress={() => {
              track({ event: mixpanelEvents.OPEN_GET_SUPPORT })
              Linking.openURL(config?.helpCenterUrl || 'https://provider-support.lyrahealth.com/')
            }}
            hasInAppCalendarSettings={hasInAppCalendarSettings}
            onSearchPress={() => openGoSearch()}
            onSettingPress={() => {
              track({ event: mixpanelEvents.OPEN_SETTINGS })
              navigate(SETTINGS.route)
            }}
            videoSessionOpen={videoSessionOpen}
            appLocked={appLocked}
            videoSessionBannerHeight={videoSessionBannerHeight}
            hideSearchNav={config?.hideSearchNav}
            hideSettingsNav={config?.hideSettingsNav}
            showSettingsNotificationBubble={
              isDAEngageOnOffPlatformVideoEnabled &&
              config?.supportsOnOffPlatformVideo &&
              videoPreference &&
              !hasSetVideoSessionPreference
            }
            onNavigate={(path) => navigate(path)}
            isSettingsActive={location.pathname === SETTINGS.route}
          />
        )}
      </ApplicationHeaderContainer>
      {isDevMocksModalOpen && <DevMocksModal visible onRequestClose={() => setIsDevMocksModalOpen(false)} />}
    </>
  )
}

HeaderContainer.propTypes = {
  videoSessionOpen: PropTypes.bool,
  appLocked: PropTypes.bool,
  supervisorSelectProvider: PropTypes.func,
  getProvider: PropTypes.func,
  getProviderExpeditedBookingConfig: PropTypes.func,
  fetchPracticeProviders: PropTypes.func,
}

const mapStateToProps = (state: RootState) => ({
  videoSessionOpen: getLTVideoSessionOpen(state),
  appLocked: getIsPrivacyGuardActive(state),
})

export default connect(mapStateToProps, {
  supervisorSelectProvider,
  getProvider,
  getProviderExpeditedBookingConfig,
  clearSelectedClientStore,
  fetchPracticeProviders,
  setSelectedTimeZone,
})(HeaderContainer)
